import {message} from 'antd';
import Form from 'antd/es/form';
import {useForm} from 'antd/es/form/Form';
import {UploadChangeParam} from 'antd/es/upload';
import {UploadFile} from 'antd/es/upload/interface';
import {debounce} from 'lodash-es';
import React, {FC} from 'react';
import {v4 as uuid} from 'uuid';

import {UploadDropzone} from 'components/@common/inputs/upload-dropzone';
import {IColumnFormatterProps} from 'components/table/table-types';
import {TFileInfo} from 'modules/data';
import {setDocumentScans} from 'modules/data/data-actions';
import {EEntityType} from 'shared/constants/entities';
import {useAppDispatch} from 'store/config/hooks';
import {fetchDocumentScansById, uploadAttachedToDocument} from 'store/slices/documents-uploading-slice';
import {EUserRoleFunction} from 'store/slices/user-slice/user-slice-role-functions';

import {UploadScanFilesItem} from './upload-scan-files-item';

interface IUploadScanFilesModalProps extends IColumnFormatterProps {
    entityName: string;
    entityType: EEntityType | undefined;
    hasUserRoleFunction: (func: EUserRoleFunction) => boolean;
    isDisabledToDelete?: boolean;
}

export const UploadScanFilesModal: FC<IUploadScanFilesModalProps> = (
    {
        record,
        entityType,
        entityName,
        hasUserRoleFunction,
        isDisabledToDelete,
    }: IUploadScanFilesModalProps,
) => {
    const dispatch = useAppDispatch();
    const [form] = useForm();

    const scanDocumentAttachments = (record?.scanDocumentAttachments ?? []) as TFileInfo[];

    const handleUploadFiles = debounce(async (files: UploadChangeParam<UploadFile<any>>) => {
        if (files.fileList && record.id) {
            const uploadAttachedPromises = files.fileList
                .filter((file: UploadFile) => !!file.originFileObj)
                .map((file: UploadFile) => uploadAttachedToDocument({
                    docId: record.id as string,
                    file: file.originFileObj as File,
                }));

            const messageKey = uuid();

            message.loading({
                content: <span className="ml-1">Загрузка файлов-приложений...</span>,
                key: messageKey,
                duration: 0,
            });

            try {
                await Promise.all(uploadAttachedPromises);

                const scans = (await fetchDocumentScansById({docId: record.id as string})).data;

                dispatch(setDocumentScans({
                    entityName,
                    entityType,
                    scans,
                    docId: record.id as string,
                }));

                message.success({
                    content: <span className="ml-1">Файлы успешно загружены</span>,
                    key: messageKey,
                    duration: 2,
                });
            } catch (e) {
                message.error({
                    content: <span className="ml-1">Ошибка загрузки файлов</span>,
                    key: messageKey,
                    duration: 2,
                });
            }
        }
    }, 0);

    return (
        <div className="d-flex gap-2 flex-column">
            <div className="d-flex gap-1 flex-column">
                {scanDocumentAttachments.map(scan => (
                    <UploadScanFilesItem
                        key={scan.attachmentId}
                        scan={scan}
                        docId={record.id as string}
                        isDisabledToDelete={isDisabledToDelete}
                        {...{entityName, entityType, hasUserRoleFunction}}
                    />
                ))}
            </div>

            {hasUserRoleFunction(EUserRoleFunction.UPLOAD_PDF) && (
                <Form
                    layout="vertical"
                    form={form}
                >
                    <Form.Item name="files">
                        <UploadDropzone
                            clearAfterChange
                            handleOnChange={handleUploadFiles}
                        />
                    </Form.Item>
                </Form>
            )}
        </div>
    );
};
